import React, { useState, useEffect, useRef } from 'react';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import toast, { Toaster } from 'react-hot-toast';
import { useI18n } from './context/i18n';
import './dashboard-styles.css';
import EChartRenderer from './EChartRenderer';
import DateRangePicker from './components/DateRangePicker';

const API_BASE_URL = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';

// Cache en memoria mientras la SPA permanezca abierta
const dashboardDataCache = {};


// Iconos SVG optimizados
const ChartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="14" width="4" height="7" rx="1" fill="currentColor"/>
    <rect x="10" y="9" width="4" height="12" rx="1" fill="currentColor"/>
    <rect x="17" y="5" width="4" height="16" rx="1" fill="currentColor"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    <circle cx="17" cy="9" r="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6M15 20c0-2.5 2-4.5 5-4.5s5 2 5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M2 7l10 7 10-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M22 7L13.5 15.5L8.5 10.5L2 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 7h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 6v12M9 9h4.5c1.1 0 2 .9 2 2s-.9 2-2 2H9.5c-1.1 0-2 .9-2 2s.9 2 2 2H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ShoppingCartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="21" r="1" fill="currentColor"/>
    <circle cx="20" cy="21" r="1" fill="currentColor"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Componente Skeleton Loader para KPI
const SkeletonKPI = () => (
  <div className="kpi-card skeleton-card">
    <div className="kpi-header">
      <div className="skeleton skeleton-icon"></div>
      <div className="skeleton skeleton-trend"></div>
    </div>
    <div className="kpi-content">
      <div className="skeleton skeleton-title"></div>
      <div className="skeleton skeleton-value"></div>
      <div className="skeleton skeleton-subtitle"></div>
    </div>
  </div>
);

// Componente de tarjeta KPI mejorado con sparkline y comparación
const KPICard = ({ title, value, subtitle, icon, trend, color = 'primary', delay = 0, sparklineData, comparison }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // Detectar si el valor es N/A o no disponible
  const isNA = value === 'N/A' || value === '-' || value === null || value === undefined;

  return (
    <div className={`kpi-card kpi-${color} ${isVisible ? 'fade-in' : ''} ${isNA ? 'kpi-disabled' : ''}`} style={{ animationDelay: `${delay}ms` }}>
      <div className="kpi-header">
        <div className="kpi-icon-wrapper">
          <div className="kpi-icon">{icon}</div>
        </div>
        {trend && !isNA && (
          <div className={`kpi-trend kpi-trend-${trend.direction}`}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path 
                d={trend.direction === 'up' ? 'M7 10V3M4 6l3-3 3 3' : 'M7 3v7M4 7l3 3 3-3'}
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span>{trend.value}</span>
          </div>
        )}
      </div>
      <div className="kpi-content">
        <h3 className="kpi-title">{title}</h3>
        <div className={`kpi-value ${isNA ? 'na' : ''}`}>{isNA ? 'N/A' : value}</div>
        {subtitle && <p className="kpi-subtitle">{subtitle}</p>}
        
        {/* Badge de comparación - solo si no es N/A */}
        {comparison && !isNA && (
          <div className={`kpi-comparison ${comparison.type}`}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path 
                d={comparison.type === 'positive' ? 'M6 8V3M4 5l2-2 2 2' : 'M6 3v5M4 6l2 2 2-2'}
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span>{comparison.text}</span>
          </div>
        )}
        
        {/* Sparkline miniatura */}
        {sparklineData && sparklineData.length > 0 && (
          <div className="kpi-sparkline">
            <Sparklines data={sparklineData} width={100} height={30} margin={5}>
              <SparklinesLine 
                color={color === 'success' ? '#10b981' : color === 'warning' ? '#f59e0b' : color === 'info' ? '#3b82f6' : '#8b5cf6'} 
                style={{ strokeWidth: 2, fill: 'none' }}
              />
            </Sparklines>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente wrapper para gráficos del Dashboard con header y botón de descarga
const DashboardChart = ({ title, description, children, onDownload }) => {
  const { language } = useI18n();
  const chartRef = useRef(null);
  
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      // Intentar descargar como PNG
      const chartInstance = chartRef.current?.querySelector('canvas');
      if (chartInstance) {
        const link = document.createElement('a');
        link.download = `${title.replace(/\s+/g, '_')}.png`;
        link.href = chartInstance.toDataURL('image/png');
        link.click();
      }
    }
  };
  
  return (
    <div className="chart-card" ref={chartRef}>
      <div className="chart-header-row">
        <div className="chart-header-text">
          <h3 className="chart-title">{title}</h3>
          {description && <p className="chart-description">{description}</p>}
        </div>
        <button className="chart-download-btn" onClick={handleDownload} title={language === 'en' ? 'Download PNG' : 'Descargar PNG'}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 10V2M5 7l3 3 3-3M2 12v2h12v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      <div className="chart-content">
        {children}
      </div>
    </div>
  );
};

// Componente de gráfico placeholder mejorado
const ChartPlaceholder = ({ title, type = 'bar', height = '300px', description }) => {
  const { language } = useI18n();
  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
        {description && <p className="chart-description">{description}</p>}
      </div>
      <div className="chart-placeholder" style={{ height }}>
        <div className="placeholder-content">
          <div className="placeholder-icon">
            <ChartIcon />
          </div>
          <p className="placeholder-label">{language === 'en' ? `Chart: ${type}` : `Gráfico de ${type}`}</p>
          <span className="placeholder-note">{language === 'en' ? 'Data will load automatically when the API is connected' : 'Los datos se cargarán automáticamente cuando conectes la API'}</span>
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ companyInfo, shopifyInitialSync }) => {
  const { language } = useI18n();

  const fixMojibake = (value) => {
    const text = String(value || '');
    try {
      return decodeURIComponent(escape(text));
    } catch {
      return text;
    }
  };

  const normalizeTextKey = (value) =>
    fixMojibake(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[¿?¡!.,:;()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const backendTextToEnglish = {
    'ventas netas': 'Net sales',
    'ventas totales': 'Total sales',
    'del periodo': 'In period',
    'ventas brutas': 'Gross sales',
    brutas: 'Gross sales',
    'ticket promedio': 'Average order value',
    'por pedido': 'Per order',
    'total pedidos': 'Total orders',
    'pagados y reembolsados': 'Paid and refunded',
    devoluciones: 'Returns',
    '0.00% de ventas brutas': '0.00% of gross sales',
    'desglose total de ventas': 'Total sales breakdown',
    'clientes activos': 'Active customers',
    'en el periodo': 'In period',
    'nuevos clientes': 'New customers',
    'primera compra en periodo': 'First purchase in period',
    'clv promedio': 'Average CLV',
    'tasa recurrencia': 'Repeat rate',
    'clientes con 2+ compras': 'Customers with 2+ purchases',
    'clientes por segmento rfm': 'Customers by RFM Segment',
    'clientes por nivel': 'Customers by Level',
    'distribucion por frecuencia': 'Distribution by Frequency',
    'distribución por frecuencia': 'Distribution by Frequency',
    'evolucion de ventas totales': 'Total Sales Evolution',
    'evolución de ventas totales': 'Total Sales Evolution',
    'ventas totales por canal': 'Total Sales by Channel',
    'ventas totales por estado': 'Total Sales by State',
    'evolucion de ventas netas': 'Net Sales Evolution',
    'evolución de ventas netas': 'Net Sales Evolution',
    'ventas netas por canal': 'Net Sales by Channel',
    'ventas netas por estado': 'Net Sales by State',
    'top categorias': 'Top Categories',
    'top categorías': 'Top Categories',
    'top 10 productos': 'Top 10 Products'
  };

  const localizeBackendText = (value) => {
    if (language !== 'en') return value;
    const original = fixMojibake(value).replace(/\s+/g, ' ').trim();
    const key = normalizeTextKey(original);
    if (backendTextToEnglish[key]) return backendTextToEnglish[key];

    // Reglas por fragmentos para textos variables del backend
    if (key.includes('evolucion de ventas totales')) return 'Total Sales Evolution';
    if (key.includes('ventas totales por canal')) return 'Total Sales by Channel';
    if (key.includes('ventas totales por estado')) return 'Total Sales by State';
    if (key.includes('evolucion de ventas netas')) return 'Net Sales Evolution';
    if (key.includes('ventas netas por canal')) return 'Net Sales by Channel';
    if (key.includes('ventas netas por estado')) return 'Net Sales by State';
    if (key.includes('clientes por segmento rfm')) return 'Customers by RFM Segment';
    if (key.includes('distribucion por frecuencia')) return 'Distribution by Frequency';
    if (key.includes('top productos')) return 'Top Products';
    if (key.includes('top categorias')) return 'Top Categories';

    let translated = original;
    const replacements = [
      [/ventas totales/gi, 'Total sales'],
      [/ventas netas/gi, 'Net sales'],
      [/netas:/gi, 'Net:'],
      [/ventas brutas/gi, 'Gross sales'],
      [/devoluciones/gi, 'Returns'],
      [/clientes activos/gi, 'Active customers'],
      [/nuevos clientes/gi, 'New customers'],
      [/tasa recurrencia/gi, 'Repeat rate'],
      [/ticket promedio/gi, 'Average order value'],
      [/por pedido/gi, 'Per order']
    ];
    replacements.forEach(([pattern, replacement]) => {
      translated = translated.replace(pattern, replacement);
    });

    return translated;
  };

  const ui = {
    errorLoading: language === 'en' ? 'Could not load dashboard data' : 'No se pudieron cargar los datos del dashboard',
    preparingData: language === 'en' ? 'Preparing your data...' : 'Preparando tus datos...',
    preparingDataSubtitle: language === 'en'
      ? 'We are still building your dashboard. This can take a few minutes after connecting your store.'
      : 'Todavia estamos armando tu dashboard. Esto puede tardar unos minutos despues de conectar tu tienda.',
    updating: language === 'en' ? 'Updating data...' : 'Actualizando datos...',
    updated: language === 'en' ? 'Data updated' : 'Datos actualizados',
    updatedShort: language === 'en' ? 'Updated!' : '¡Actualizado!',
    tabCommercial: language === 'en' ? 'Commercial Performance' : 'Performance Comercial',
    tabCustomers: language === 'en' ? 'Customer Management' : 'Gestión Clientes',
    tabEmail: language === 'en' ? 'Email Marketing' : 'Marketing Email',
    tabAttribution: language === 'en' ? 'Campaign Attribution' : 'Atribución Campañas',
    salesEvolutionNet: language === 'en' ? 'Total Sales Evolution' : 'Evolución de Ventas Totales',
    salesEvolution: language === 'en' ? 'Sales Evolution' : 'Evolución de Ventas',
    salesByChannelNet: language === 'en' ? 'Total Sales by Channel' : 'Ventas Totales por Canal',
    salesByChannel: language === 'en' ? 'Sales by Channel' : 'Ventas por Canal',
    salesByStateNet: language === 'en' ? 'Total Sales by State' : 'Ventas Totales por Estado',
    salesByState: language === 'en' ? 'Sales by State' : 'Ventas por Estado',
    hideSidebar: language === 'en' ? 'Hide sidebar' : 'Ocultar barra lateral',
    showSidebar: language === 'en' ? 'Show sidebar' : 'Mostrar barra lateral',
    dashboardTitle: language === 'en' ? 'Analytics Dashboard' : 'Dashboard de Analítica',
    dashboardSubtitle: language === 'en' ? 'Key insights and business metrics' : 'Insights y métricas clave de tu negocio',
    changedTo: language === 'en' ? 'Switched to' : 'Cambiado a',
    auto: language === 'en' ? 'Auto' : 'Auto',
    refresh: language === 'en' ? 'Refresh' : 'Actualizar',
    searchKpi: language === 'en' ? 'Search KPI by name...' : 'Buscar KPI por nombre...',
    noKpisFound: language === 'en' ? 'No KPIs found matching' : 'No se encontraron KPIs que coincidan con',
    downloadPng: language === 'en' ? 'Download PNG' : 'Descargar PNG'
  };

  const [activeTab, setActiveTab] = useState('commercial');
  const [isLoading, setIsLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  // FIX onboarding (2026-08-04): true cuando el backend devuelve 404 / "aun no
  // listo" (datos todavia no construidos durante el onboarding). No es un error
  // real: mostramos "Preparando tus datos..." en vez de un toast/error.
  const [dataNotReady, setDataNotReady] = useState(false);
  const [shopifyDashboardRefreshAttempts, setShopifyDashboardRefreshAttempts] = useState(0);
  
  // Compañía activa: única fuente de verdad = App.js -> companyInfo
  const activeCompanyId = companyInfo?.company_id || shopifyInitialSync?.company_id || localStorage.getItem('companyId') || localStorage.getItem('shopifyCompanyId') || '';
  const activeCompanyName =
    companyInfo?.company_name ||
    companyInfo?.companyName ||
    activeCompanyId;
  const hasAuthenticatedCompany = !!activeCompanyId;
  
  // Estados para filtros de fecha
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareStartDate, setCompareStartDate] = useState(null);
  const [compareEndDate, setCompareEndDate] = useState(null);
  
  // Estados para auto-refresh
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(15);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Estado para búsqueda de KPIs
  const [searchTerm, setSearchTerm] = useState('');

  // Helper para iconos
  const getIconForKPI = (title) => {
    const normalizedTitle = String(title || '').toLowerCase();

    if (
      normalizedTitle.includes('ventas') ||
      normalizedTitle.includes('clv') ||
      normalizedTitle.includes('roas') ||
      normalizedTitle.includes('ticket') ||
      normalizedTitle.includes('devol') ||
      normalizedTitle.includes('reembolso') ||
      normalizedTitle.includes('refund') ||
      normalizedTitle.includes('net') ||
      normalizedTitle.includes('gross')
    ) return <DollarIcon />;

    if (normalizedTitle.includes('clientes') || normalizedTitle.includes('usuarios')) return <UsersIcon />;
    if (normalizedTitle.includes('email') || normalizedTitle.includes('open') || normalizedTitle.includes('click') || normalizedTitle.includes('bounce') || normalizedTitle.includes('ctor')) return <EmailIcon />;
    if (normalizedTitle.includes('conversiones') || normalizedTitle.includes('pedidos')) return <ShoppingCartIcon />;
    return <TrendingUpIcon />;
  };


  // Detectar scroll para reducir header
  useEffect(() => {
    const dashboardContainer = document.querySelector('.dashboard-container');
    if (!dashboardContainer) return;

    const handleScroll = () => {
      setIsScrolled(dashboardContainer.scrollTop > 50);
    };

    dashboardContainer.addEventListener('scroll', handleScroll);
    return () => dashboardContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Comunicar estado del sidebar a App.js
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('toggleSidebar', { detail: { collapsed: sidebarCollapsed } }));
  }, [sidebarCollapsed]);

  // Carga de datos real
  useEffect(() => {
    const toYMD = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Construir URL con fechas directas - usar range=custom para que el backend use start_date y end_date
        const startDateStr = toYMD(startDate);
        const endDateStr = toYMD(endDate);
        if (!activeCompanyId) {
          setDashboardData(null);
          setIsLoading(false);
          return;
        }

        let url = `${API_BASE_URL}/dashboard/${activeTab}?range=custom&company_id=${activeCompanyId}&start_date=${startDateStr}&end_date=${endDateStr}&compare_enabled=${compareEnabled}`;
        if (compareEnabled && compareStartDate && compareEndDate) {
          url += `&compare_start_date=${toYMD(compareStartDate)}&compare_end_date=${toYMD(compareEndDate)}`;
        }

        const cacheKey = url;
        const cachedData = dashboardDataCache[cacheKey];
        if (cachedData) {
          setDashboardData(cachedData);
          setIsLoading(false);
          return;
        }

        const response = await fetch(url);
        if (!response.ok) {
          const err = new Error(language === 'en' ? 'Error loading data' : 'Error al cargar datos');
          err.httpStatus = response.status;
          throw err;
        }

        // Respuesta OK: los datos existen -> ya no estamos "not ready".
        setDataNotReady(false);

        const data = await response.json();
        
        // Enriquecer KPIs con iconos
        if (data.kpis) {
          data.kpis = data.kpis.map(kpi => ({
            ...kpi,
            title: localizeBackendText(kpi.title),
            subtitle: localizeBackendText(kpi.subtitle),
            icon: getIconForKPI(kpi.title)
          }));
        }

        if (data.breakdown) {
          data.breakdown = {
            ...data.breakdown,
            title: localizeBackendText(data.breakdown.title),
            items: Array.isArray(data.breakdown.items)
              ? data.breakdown.items.map((item) => ({
                  ...item,
                  label: localizeBackendText(item.label)
                }))
              : []
          };
        }

        if (data.charts && typeof data.charts === 'object') {
          const normalizedCharts = {};
          Object.entries(data.charts).forEach(([key, chartValue]) => {
            if (chartValue && typeof chartValue === 'object' && !Array.isArray(chartValue)) {
              const datasets = Array.isArray(chartValue.datasets)
                ? chartValue.datasets.map((dataset) => ({
                    ...dataset,
                    label: localizeBackendText(dataset?.label)
                  }))
                : chartValue.datasets;

              normalizedCharts[key] = {
                ...chartValue,
                title: localizeBackendText(chartValue.title),
                datasets
              };
            } else {
              normalizedCharts[key] = chartValue;
            }
          });
          data.charts = normalizedCharts;
        }
        
        const hasRealData = Array.isArray(data?.kpis) && data.kpis.some((kpi) => {
          const value = String(kpi?.value ?? '').trim();
          return value && value !== '0' && value !== '$0' && value !== '$0.00';
        });
        if (hasRealData) {
          dashboardDataCache[cacheKey] = data;
        }
        setDashboardData(data);
        // setLastRefresh(new Date()); // ELIMINADO: Causaba bucle infinito
      } catch (error) {
        console.error("Error fetching dashboard data:", error);

        const isShopifyPreparing =
          shopifyInitialSync &&
          shopifyInitialSync.status !== 'complete' &&
          shopifyInitialSync.status !== 'dismissed';

        // 404 = los datos aun no existen (onboarding / sync incompleto). NO es
        // un error real: degradar con gracia a "Preparando tus datos..." sin
        // toast. Un 5xx (o cualquier error de red sin status) SI es un error
        // real y debe seguir mostrandose (salvo que Shopify este preparando).
        const isNotReady = error?.httpStatus === 404;
        if (isNotReady) {
          setDataNotReady(true);
        } else if (!isShopifyPreparing) {
          setDataNotReady(false);
          toast.error(ui.errorLoading);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [activeTab, startDate, endDate, lastRefresh, activeCompanyId, compareEnabled, compareStartDate, compareEndDate, language]);

  const hasRealDashboardData = Array.isArray(dashboardData?.kpis) && dashboardData.kpis.some((kpi) => {
    const value = String(kpi?.value ?? '').trim();
    return value && value !== '0' && value !== '$0' && value !== '$0.00';
  });

  useEffect(() => {
    if (!shopifyInitialSync) return undefined;
    if (shopifyInitialSync.status === 'dismissed') return undefined;
    if (hasRealDashboardData) return undefined;
    if (shopifyDashboardRefreshAttempts >= 6) return undefined;

    const timeout = window.setTimeout(() => {
      setLastRefresh(new Date());
      setShopifyDashboardRefreshAttempts((current) => current + 1);
    }, 15000);

    return () => window.clearTimeout(timeout);
  }, [
    shopifyInitialSync,
    hasRealDashboardData,
    shopifyDashboardRefreshAttempts,
  ]);

  // Auto-refresh automático
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(async () => {
      toast.loading(ui.updating, { id: 'refresh' });
      setLastRefresh(new Date()); // Trigger reload
      toast.success(ui.updated, { id: 'refresh', duration: 2000 });
    }, refreshInterval * 60 * 1000);

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, refreshInterval]);

  // Manejador de refresh manual
  const handleManualRefresh = async () => {
    toast.loading(ui.updating, { id: 'manual-refresh' });
    setLastRefresh(new Date()); // Trigger reload
    toast.success(ui.updatedShort, { id: 'manual-refresh', duration: 2000 });
  };

  const tabs = [
    { id: 'commercial', label: ui.tabCommercial, icon: <TrendingUpIcon /> },
    { id: 'customers', label: ui.tabCustomers, icon: <UsersIcon /> },
    { id: 'email', label: ui.tabEmail, icon: <EmailIcon /> },
    { id: 'attribution', label: ui.tabAttribution, icon: <TrendingUpIcon /> },
  ];

  const isShopifyModel = !!activeCompanyId;
  const salesEvolutionTitle = isShopifyModel ? ui.salesEvolutionNet : ui.salesEvolution;
  const salesByChannelTitle = isShopifyModel ? ui.salesByChannelNet : ui.salesByChannel;
  const salesByStateTitle = isShopifyModel ? ui.salesByStateNet : ui.salesByState;
  const localizedKpis = (dashboardData?.kpis || []).map((kpi) => ({
    ...kpi,
    title: localizeBackendText(kpi.title),
    subtitle: localizeBackendText(kpi.subtitle)
  }));
  const sanitizeChartTitle = (rawTitle) =>
    String(rawTitle || '')
      .replace(/\b(?:ia|ai)\b\s*[:-]?\s*/gi, '')
      .replace(/[^\p{L}\p{N}\p{M}\p{P}\p{Zs}%$]/gu, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  const getChartTitle = (chartKey, fallback) =>
    language === 'en'
      ? sanitizeChartTitle(fallback)
      : sanitizeChartTitle(localizeBackendText(dashboardData?.charts?.[chartKey]?.title || fallback));

  return (
    <div className="dashboard-container">
      {/* Toaster para notificaciones */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
      {/* Header del Dashboard */}
      <div className={`dashboard-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="dashboard-title-section">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? ui.showSidebar : ui.hideSidebar}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 10h14M3 5h14M3 15h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <div className="title-content">
            <h1>{ui.dashboardTitle}</h1>
            <p>{ui.dashboardSubtitle}</p>
          </div>
        </div>
        <div className="dashboard-controls">
          {/* Badge de empresa */}
          {hasAuthenticatedCompany && (
            <div className="company-info-display">
              <span className="company-name-badge">
                {activeCompanyName.toUpperCase()}
              </span>
            </div>
          )}

          {/* Grupo de controles de fecha y refresh */}
          <div className="controls-group">
            
            {/* Selector de rango de fecha */}
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
              compareEnabled={compareEnabled}
              onCompareChange={(enabled) => {
                setCompareEnabled(enabled);
                if (!enabled) {
                  setCompareStartDate(null);
                  setCompareEndDate(null);
                }
              }}
              compareStartDate={compareStartDate}
              compareEndDate={compareEndDate}
              onCompareRangeChange={(start, end) => {
                setCompareStartDate(start);
                setCompareEndDate(end);
              }}
            />
            
            {/* Auto-refresh toggle */}
            <label className="refresh-toggle">
              <input 
                type="checkbox" 
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
              />
              <span className="toggle-label">Auto</span>
            </label>
          </div>
          
          {/* Botón de refresh manual */}
          <button className="refresh-button" onClick={handleManualRefresh}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M16 4v4h-4M2 14v-4h4M16.364 7.636A7 7 0 1 0 4.636 14.364M1.636 10.364a7 7 0 0 0 11.728 3.272" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {ui.refresh}
          </button>
        </div>
      </div>

      {/* Tabs de navegación */}
      <div className="dashboard-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Contenido del tab activo */}
      <div className="dashboard-content">
        {/* FIX onboarding (2026-08-04): datos aun no construidos (404). Mostramos
            un estado "Preparando tus datos..." en vez de un error. */}
        {dataNotReady && !isLoading && !hasRealDashboardData && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '48px 24px',
            color: '#6b7280',
            gap: 14,
          }}>
            <div style={{
              width: 40,
              height: 40,
              border: '4px solid #E9D8FD',
              borderTopColor: '#710FE5',
              borderRadius: '50%',
              animation: 'upsellerate-spin 1s linear infinite',
            }} />
            <div style={{ color: '#710FE5', fontSize: 15, fontWeight: 800 }}>
              {ui.preparingData}
            </div>
            <div style={{ maxWidth: 420, fontSize: 13, lineHeight: 1.6 }}>
              {ui.preparingDataSubtitle}
            </div>
            <style>{`
              @keyframes upsellerate-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        {/* Barra de búsqueda de KPIs */}
        <div className="kpi-search-bar">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
            <path d="M12.5 12.5l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="search"
            placeholder={ui.searchKpi}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="kpi-search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        {/* KPI Cards Grid */}
        <div className="kpi-grid">
          {isLoading ? (
            // Mostrar skeletons mientras carga
            Array.from({ length: 4 }).map((_, index) => (
              <SkeletonKPI key={index} />
            ))
          ) : (
            // Filtrar KPIs según búsqueda
            localizedKpis
              .filter(kpi => 
                kpi.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (kpi.subtitle || '').toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((kpi, index) => (
                <KPICard key={index} {...kpi} delay={index * 100} />
              ))
          )}
          
          {/* Mensaje cuando no hay resultados */}
          {!isLoading && searchTerm && localizedKpis.filter(kpi => 
            kpi.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (kpi.subtitle || '').toLowerCase().includes(searchTerm.toLowerCase())
          ).length === 0 && (
            <div className="no-results">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2"/>
                <path d="M24 14v14M24 32v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p>{ui.noKpisFound} "{searchTerm}"</p>
            </div>
          )}
        </div>

        {activeTab === 'commercial' && dashboardData?.breakdown?.items?.length > 0 && (
          <div className="sales-breakdown-panel">
            <div className="sales-breakdown-card">
              <h3 className="sales-breakdown-title">
                {localizeBackendText(dashboardData?.breakdown?.title) || 'Total sales breakdown'}
              </h3>
              <div className="sales-breakdown-items">
                {dashboardData.breakdown.items.map((item, index) => (
                  <div
                    key={`${item.label}-${index}`}
                    className={`sales-breakdown-row ${/^(total sales|ventas totales)$/i.test(String(item.label || '')) ? 'is-total' : ''}`}
                  >
                    <span className="sales-breakdown-label">{localizeBackendText(item.label)}</span>
                    <span className={`sales-breakdown-value ${Number(item.value) < 0 ? 'negative' : 'positive'}`}>
                      {item.display || '$0.00'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Gráficos según el tab activo */}
        {activeTab === 'commercial' && (
          <div className="charts-section">
            <div className="charts-row">
              <div className="chart-full">
                {dashboardData?.charts?.sales_evolution ? (
                  <DashboardChart title={getChartTitle('sales_evolution', salesEvolutionTitle)}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('sales_evolution', salesEvolutionTitle),
                        chart: 'line',
                        data: dashboardData.charts.sales_evolution
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={salesEvolutionTitle}
                    type={language === 'en' ? 'line' : 'línea'} 
                    height="350px"
                    description={isShopifyModel
                      ? (language === 'en' ? 'Historical net sales trend (after refunds)' : 'Tendencia histórica de ventas netas (después de reembolsos)')
                      : (language === 'en' ? 'Historical revenue trend by period' : 'Tendencia histórica de ingresos por período')}
                  />
                )}
              </div>
            </div>
            <div className="charts-row">
              <div className="chart-half">
                {dashboardData?.charts?.sales_by_channel ? (
                  <DashboardChart title={getChartTitle('sales_by_channel', salesByChannelTitle)}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('sales_by_channel', salesByChannelTitle),
                        chart: 'bar',
                        data: dashboardData.charts.sales_by_channel
                      }} 
                    />
                  </DashboardChart>
                ) : dashboardData?.charts?.top_categories ? (
                  <DashboardChart title={getChartTitle('top_categories', language === 'en' ? 'Top Categories' : 'Top Categorías')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('top_categories', language === 'en' ? 'Top Categories' : 'Top Categorías'),
                        chart: 'bar',
                        data: dashboardData.charts.top_categories
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={salesByChannelTitle}
                    type={language === 'en' ? 'donut' : 'dona'} 
                    height="300px"
                    description={isShopifyModel
                      ? (language === 'en' ? 'Net sales distribution by UTM channel' : 'Distribución de ventas netas por canal UTM')
                      : (language === 'en' ? 'Sales distribution by UTM channel' : 'Distribución de ventas por canal UTM')}
                  />
                )}
              </div>
              <div className="chart-half">
                {dashboardData?.charts?.top_products ? (
                  <DashboardChart title={getChartTitle('top_products', language === 'en' ? 'Top 10 Products' : 'Top 10 Productos')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('top_products', language === 'en' ? 'Top 10 Products' : 'Top 10 Productos'),
                        chart: 'bar',
                        data: dashboardData.charts.top_products
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Top 10 Products' : 'Top 10 Productos'} 
                    type={language === 'en' ? 'bars' : 'barras'} 
                    height="300px"
                    description={language === 'en' ? 'Products with highest revenue' : 'Productos con mayor revenue'}
                  />
                )}
              </div>
            </div>
            <div className="charts-row">
              <div className="chart-full">
                {dashboardData?.charts?.sales_by_state ? (
                  <DashboardChart title={getChartTitle('sales_by_state', salesByStateTitle)}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('sales_by_state', salesByStateTitle),
                        chart: 'bar',
                        data: dashboardData.charts.sales_by_state
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={salesByStateTitle}
                    type={language === 'en' ? 'bars' : 'barras'} 
                    height="300px"
                    description={isShopifyModel
                      ? (language === 'en' ? 'Geographic distribution of net sales' : 'Distribución geográfica de ventas netas')
                      : (language === 'en' ? 'Geographic distribution of sales' : 'Distribución geográfica de ventas')}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="charts-section">
            <div className="charts-row">
              <div className="chart-half">
                {dashboardData?.charts?.customers_by_segment ? (
                  <DashboardChart title={getChartTitle('customers_by_segment', language === 'en' ? 'Customers by RFM Segment' : 'Clientes por Segmento RFM')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('customers_by_segment', language === 'en' ? 'Customers by RFM Segment' : 'Clientes por Segmento RFM'),
                        chart: 'bar',
                        data: dashboardData.charts.customers_by_segment
                      }} 
                    />
                  </DashboardChart>
                ) : dashboardData?.charts?.customers_by_level ? (
                  <DashboardChart title={getChartTitle('customers_by_level', language === 'en' ? 'Customers by Level' : 'Clientes por Nivel')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('customers_by_level', language === 'en' ? 'Customers by Level' : 'Clientes por Nivel'),
                        chart: 'bar',
                        data: dashboardData.charts.customers_by_level
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Customers by Segment' : 'Clientes por Segmento'} 
                    type={language === 'en' ? 'donut' : 'dona'} 
                    height="350px"
                    description={language === 'en' ? 'Customer distribution by RFM segment' : 'Distribución de clientes por segmento RFM'}
                  />
                )}
              </div>
              <div className="chart-half">
                {dashboardData?.charts?.customers_by_frequency ? (
                  <DashboardChart title={getChartTitle('customers_by_frequency', language === 'en' ? 'Distribution by Frequency' : 'Distribución por Frecuencia')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('customers_by_frequency', language === 'en' ? 'Distribution by Frequency' : 'Distribución por Frecuencia'),
                        chart: 'bar',
                        data: dashboardData.charts.customers_by_frequency
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Distribution by Frequency' : 'Distribución por Frecuencia'} 
                    type={language === 'en' ? 'bars' : 'barras'} 
                    height="350px"
                    description={language === 'en' ? 'Customers by number of purchases' : 'Clientes por número de compras'}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="charts-section">
            <div className="charts-row">
              <div className="chart-full">
                {dashboardData?.charts?.email_evolution ? (
                  <DashboardChart title={getChartTitle('email_evolution', language === 'en' ? 'Email Marketing Evolution' : 'Evolución de Email Marketing')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('email_evolution', language === 'en' ? 'Email Marketing Evolution' : 'Evolución de Email Marketing'),
                        chart: 'line',
                        data: dashboardData.charts.email_evolution
                      }} 
                    />
                  </DashboardChart>
                ) : dashboardData?.charts?.email_metrics_evolution ? (
                  <DashboardChart title={getChartTitle('email_metrics_evolution', language === 'en' ? 'Email Metrics Evolution' : 'Evolución de Métricas Email')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('email_metrics_evolution', language === 'en' ? 'Email Metrics Evolution' : 'Evolución de Métricas Email'),
                        chart: 'line',
                        data: dashboardData.charts.email_metrics_evolution
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Email Marketing Evolution' : 'Evolución de Email Marketing'} 
                    type={language === 'en' ? 'multi-line' : 'línea múltiple'} 
                    height="350px"
                    description={language === 'en' ? 'Sent, Opens and Clicks over time' : 'Enviados, Aperturas y Clicks en el tiempo'}
                  />
                )}
              </div>
            </div>
            <div className="charts-row">
              <div className="chart-full">
                {dashboardData?.charts?.campaigns_performance ? (
                  <DashboardChart title={getChartTitle('campaigns_performance', language === 'en' ? 'Campaign Performance' : 'Performance por Campaña')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('campaigns_performance', language === 'en' ? 'Campaign Performance' : 'Performance por Campaña'),
                        chart: 'bar',
                        data: dashboardData.charts.campaigns_performance
                      }} 
                    />
                  </DashboardChart>
                ) : dashboardData?.charts?.top_campaigns ? (
                  <DashboardChart title={getChartTitle('top_campaigns', language === 'en' ? 'Top Campaigns' : 'Top Campañas')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('top_campaigns', language === 'en' ? 'Top Campaigns' : 'Top Campañas'),
                        chart: 'bar',
                        data: dashboardData.charts.top_campaigns
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Campaign Performance' : 'Performance por Campaña'} 
                    type={language === 'en' ? 'bars' : 'barras'} 
                    height="350px"
                    description={language === 'en' ? 'Campaigns with highest engagement' : 'Campañas con mayor engagement'}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attribution' && (
          <div className="charts-section">
            <div className="charts-row">
              <div className="chart-half">
                {dashboardData?.charts?.revenue_by_source ? (
                  <DashboardChart title={getChartTitle('revenue_by_source', language === 'en' ? 'Net Revenue by UTM Source' : 'Ingresos Netos por Fuente UTM')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('revenue_by_source', language === 'en' ? 'Net Revenue by UTM Source' : 'Ingresos Netos por Fuente UTM'),
                        chart: 'bar',
                        data: dashboardData.charts.revenue_by_source
                      }} 
                    />
                  </DashboardChart>
                ) : dashboardData?.charts?.sales_by_campaign ? (
                  <DashboardChart title={getChartTitle('sales_by_campaign', language === 'en' ? 'Sales by Campaign' : 'Ventas por Campaña')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('sales_by_campaign', language === 'en' ? 'Sales by Campaign' : 'Ventas por Campaña'),
                        chart: 'bar',
                        data: dashboardData.charts.sales_by_campaign
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Net Revenue by Source' : 'Ingresos Netos por Fuente'} 
                    type={language === 'en' ? 'donut' : 'dona'} 
                    height="350px"
                    description={language === 'en' ? 'Net revenue distribution by traffic source' : 'Distribución de ingresos netos por fuente de tráfico'}
                  />
                )}
              </div>
              <div className="chart-half">
                {dashboardData?.charts?.email_attribution ? (
                  <DashboardChart title={getChartTitle('email_attribution', language === 'en' ? 'Email Attribution (Last Click 7d)' : 'Atribución Email (Last Click 7d)')}>
                    <EChartRenderer 
                      hideTitle
                      viz={{
                        type: 'visualization',
                        title: getChartTitle('email_attribution', language === 'en' ? 'Email Attribution' : 'Atribución Email'),
                        chart: 'bar',
                        data: dashboardData.charts.email_attribution
                      }} 
                    />
                  </DashboardChart>
                ) : (
                  <ChartPlaceholder 
                    title={language === 'en' ? 'Email Attribution' : 'Atribución Email'} 
                    type={language === 'en' ? 'bars' : 'barras'} 
                    height="350px"
                    description={language === 'en' ? 'Revenue attributed to email campaigns' : 'Revenue atribuido a campañas de email'}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
